"use client";

import { useEffect, useMemo, useRef, type ComponentType } from "react";
import type {
  LiveOptimizerPageProps,
  OptimizerPageClientProps,
  OptimizerSportsbookPreferenceRuntime,
} from "@/components/optimizer/optimizer-page-client";
import { useSportsbookPreferences } from "@/contexts/sportsbook-preferences-context";
import {
  getSingleSportsbookDefaults,
  getSurfaceDefaultBookIds,
  resolveSingleSportsbookSelection,
} from "@/lib/sportsbook-preferences";
import type { PlatformType } from "@/components/optimizer/utils-v2";
import { sportsbookIdToDisplay } from "@/lib/sportsbook-registry";
import { useToast } from "@/contexts/toast-context";
import { useComparisonOrder } from "@/hooks/use-comparison-order";

export function DeferredOptimizerPageClient({
  Page,
  ...props
}: OptimizerPageClientProps & { Page: ComponentType<LiveOptimizerPageProps> }) {
  const { state, actions, meta } = useSportsbookPreferences();
  const { showToast } = useToast();
  const fallbackNoticeRef = useRef<string | null>(null);
  const surface = props.sportsbookSurface;
  const preferencesConfigured =
    meta.status === "ready"
      ? meta.isConfigured
      : meta.status === "loading"
        ? Boolean(props.sportsbookPreferencesConfigured)
        : false;
  const myBooks = useMemo<readonly PlatformType[] | undefined>(() => {
    if (!surface || !preferencesConfigured || !state.profile) return undefined;
    return getSurfaceDefaultBookIds(state.profile, surface) as PlatformType[];
  }, [preferencesConfigured, state.profile, surface]);
  const defaultBookIds = useMemo<readonly PlatformType[] | undefined>(
    () => (surface ? getSingleSportsbookDefaults(preferencesConfigured ? state.profile : null, surface) : undefined),
    [preferencesConfigured, state.profile, surface],
  );
  const singleResolution = useMemo(() => {
    if (!surface || !state.profile || !preferencesConfigured) return null;
    return resolveSingleSportsbookSelection(
      state.profile,
      state.overrides[surface],
      surface,
    );
  }, [preferencesConfigured, state.overrides, state.profile, surface]);
  const { order: comparisonPriorityOrder, columns: comparisonColumns, runtime: comparisonOrderRuntime } = useComparisonOrder(props.comparisonPriorityOrder);

  useEffect(() => {
    const unavailableBookId = singleResolution?.unavailableBookId;
    const fallbackBookId = singleResolution?.bookId;
    if (!surface || !unavailableBookId || !fallbackBookId) return;
    const noticeKey = `${surface}:${unavailableBookId}:${fallbackBookId}`;
    if (fallbackNoticeRef.current === noticeKey) return;
    fallbackNoticeRef.current = noticeKey;
    showToast(
      `${sportsbookIdToDisplay(unavailableBookId)} is not available for this tool. Showing ${sportsbookIdToDisplay(fallbackBookId)} instead.`,
      "info",
      "Book unavailable",
      5000,
    );
  }, [showToast, singleResolution, surface]);
  const sportsbookPreferenceRuntime = useMemo<OptimizerSportsbookPreferenceRuntime | undefined>(() => {
    if (!surface || meta.status !== "ready" || !meta.isConfigured || !singleResolution) {
      return undefined;
    }

    return {
      effectiveBookId: singleResolution.bookId,
      saveSelection: async (bookId) =>
        (await actions.saveSingleSelection(surface, bookId)).success,
    };
  }, [actions, meta, singleResolution, surface]);
  return (
    <Page
      {...props}
      myBooks={myBooks}
      defaultBookIds={defaultBookIds}
      sportsbookPreferencesConfigured={preferencesConfigured}
      sportsbookPreferenceRuntime={sportsbookPreferenceRuntime}
      comparisonPriorityOrder={comparisonPriorityOrder}
      comparisonOrderRuntime={comparisonOrderRuntime}
      comparisonColumns={comparisonColumns}
    />
  );
}
